/**
 * Shared completion-progress helpers used by ReportProgressCard,
 * EditorNavigationContext (save payload), and Criteria (checkmark).
 */

/**
 * Per-chapter progress: how many criterion-components have a conformance level set.
 * @returns {{ status: string, filled: number, total: number }}
 */
export function getChapterProgress(chapterId, evaluation, catalogChapter) {
  if (!catalogChapter) return { status: "not-started", filled: 0, total: 0 };
  const chapterData = evaluation?.chapters?.[chapterId];
  if (chapterData?.disabled) return { status: "disabled", filled: 0, total: 0 };

  const criteriaList = catalogChapter.criteria || [];
  let total = 0;
  let filled = 0;

  criteriaList.forEach((crit) => {
    const components = crit.components || [];
    const evalCrit = chapterData?.criteria?.find(
      (c) => String(c.num) === String(crit.id)
    );
    components.forEach((comp) => {
      total++;
      const compName = typeof comp === "string" ? comp : comp?.name;
      const evalComp = evalCrit?.components?.find((c) => c.name === compName);
      if (evalComp?.adherence?.level) filled++;
    });
  });

  if (total === 0) return { status: "not-started", filled: 0, total: 0 };
  if (filled === 0) return { status: "not-started", filled, total };
  if (filled === total) return { status: "complete", filled, total };
  return { status: "in-progress", filled, total };
}

/**
 * Overall completion percentage across every chapter in the catalog.
 * @returns {{ filled: number, total: number, percentage: number }}
 */
export function getOverallCompletionPercentage(evaluation, catalogChapters) {
  if (!catalogChapters?.length) return { filled: 0, total: 0, percentage: 0 };

  let totalFilled = 0;
  let totalAll = 0;

  catalogChapters.forEach((ch) => {
    const progress = getChapterProgress(ch.id, evaluation, ch);
    totalFilled += progress.filled;
    totalAll += progress.total;
  });

  const percentage =
    totalAll > 0 ? Math.round((totalFilled / totalAll) * 100) : 0;
  return { filled: totalFilled, total: totalAll, percentage };
}

/**
 * True when every component in the criterion has a conformance level selected.
 * Checks against the catalog-defined component list so the checkmark only
 * appears once ALL expected components have been filled out, not just the ones
 * the user has interacted with.
 */
export function isCriterionFilledOut(chapterId, criterionId, evaluation, catalogComponents) {
  const chapterData = evaluation?.chapters?.[chapterId];
  const evalCrit = chapterData?.criteria?.find(
    (c) => String(c.num) === String(criterionId)
  );
  if (!evalCrit?.components?.length) return false;

  if (catalogComponents?.length) {
    return catalogComponents.every((comp) => {
      const compName = typeof comp === "string" ? comp : comp?.name;
      const evalComp = evalCrit.components.find((c) => c.name === compName);
      return evalComp?.adherence?.level && String(evalComp.adherence.level).trim();
    });
  }

  return evalCrit.components.every(
    (comp) => comp.adherence?.level && String(comp.adherence.level).trim()
  );
}
